import { Debit, Tariff } from 'interfaces';
import { v4 } from 'uuid';
import {
  addMinutesToDateTime,
  parseDateTime,
  toDateMillis,
} from 'utils/dateUtils';
import { DEFAULT_CLAIM_TARIFF_STATUS } from '@billing/validation/tariff';
import { DateTime } from 'luxon';

export type RowItem = Tariff & {
  _rowID: string;
  units: number;
  icd9: string;
  percentage: number;
  split: string;
  incision?: string | null;
  anesthesiaUnits?: number;
  isAnesthesiaUnitsManual?: boolean;
  total: number;
  isManualTotal: boolean;
  isDirty: boolean;
  serviceStartOverridden?: boolean;
  serviceEndOverridden?: boolean;
};

const cadFormatter = new Intl.NumberFormat(undefined, {
  style: 'currency',
  currency: 'CAD',
});

export const formatCad = (value: number | string | undefined) =>
  cadFormatter.format(Number(value ?? 0));

const percentFormatter = new Intl.NumberFormat(undefined, { style: 'percent' });
export const formatPercent = (value: number | string | undefined) =>
  percentFormatter.format(Number(value ?? 0));

export const computeTotalFromRatePct = (rate: number | string, pct?: number) =>
  Number((Number(rate) * ((pct ?? 100) / 100)).toFixed(2));

type TariffDateTimeField = 'serviceStart' | 'serviceEnd';

type RowIDLookup = {
  idsByTariffID: Map<string, string[]>;
  draftRowIDs: string[];
  rowsByRowID: Map<string, RowItem>;
};

// Parses a tariff date time string into DateTime in UTC, or null if invalid.
const parseTariffDateTime = (value?: string) => {
  if (!value) return null;

  const dateTime = parseDateTime(value, { setZone: true });
  return dateTime.isValid ? dateTime.toUTC() : null;
};

export const normalizeTariffDateTime = (value?: string) =>
  parseTariffDateTime(value)?.toISO() ?? '';

export const getDefaultTariffServiceEnd = (serviceStart?: string) =>
  addMinutesToDateTime(serviceStart, 15);

const clampAnesthesiaUnits = (value: number) =>
  Math.max(0, Math.min(96, Math.ceil(value)));

export const calculateAnesthesiaUnitsFromTimes = (
  serviceStart?: string | null,
  serviceEnd?: string | null
) => {
  const start = parseTariffDateTime(serviceStart ?? undefined);
  const end = parseTariffDateTime(serviceEnd ?? undefined);
  if (!start || !end || end <= start) return 0;
  const minutes = end.diff(start, 'minutes').minutes;
  return clampAnesthesiaUnits(minutes / 15);
};

export const applyTimeToDateTime = (
  baseDateTime: string | undefined,
  timeValue: string,
  timezone?: string | null
) => {
  if (!baseDateTime) return '';

  // Accepts both "HH:mm" and "H:mm" formats (e.g. "09:30" and "9:30", 24-hour ).
  let parsedTime = DateTime.fromFormat(timeValue, 'HH:mm');
  if (!parsedTime.isValid) {
    parsedTime = DateTime.fromFormat(timeValue, 'H:mm');
  }
  if (!parsedTime.isValid) return '';

  const zone = timezone?.trim() || 'America/Winnipeg';
  const baseDate = parseTariffDateTime(baseDateTime)?.setZone(
    zone
  );
  if (!baseDate) return '';

  return (
    baseDate
      .set({
        hour: parsedTime.hour,
        minute: parsedTime.minute,
        second: 0,
        millisecond: 0,
      })
      .toUTC()
      .toISO() ?? ''
  );
};

export const getTariffServiceTimes = (
  tariff: Pick<Tariff, TariffDateTimeField>,
  claimServiceStart?: string,
  claimServiceEnd?: string
) => {
  // Persisted tariff times win; otherwise inherit timing from the parent claim.
  const serviceStart =
    normalizeTariffDateTime(tariff.serviceStart) ||
    normalizeTariffDateTime(claimServiceStart);
  const serviceEnd =
    normalizeTariffDateTime(tariff.serviceEnd) ||
    normalizeTariffDateTime(claimServiceEnd) ||
    getDefaultTariffServiceEnd(serviceStart);

  return {
    serviceStart,
    serviceEnd,
  };
};

export const getNextTariffDateTime = (
  tariffs: Array<Pick<Tariff, TariffDateTimeField>> = [],
  claimDateTime?: string,
  field: TariffDateTimeField = 'serviceStart'
) => {
  // New draft rows continue from the most recent row timing instead of
  // resetting back to the claim start every time.
  const latestTariffDateTime = [...tariffs]
    .reverse()
    .find((tariff) => !!tariff[field]);

  return (
    normalizeTariffDateTime(latestTariffDateTime?.[field]) ||
    normalizeTariffDateTime(claimDateTime)
  );
};

const appendRowID = (
  map: Map<string, string[]>,
  key: string,
  rowID: string
) => {
  map.set(key, [...(map.get(key) || []), rowID]);
};

const takeNextRowID = (rowIDs: string[] | undefined) => rowIDs?.shift() || '';

// Rebuilds the previous-row used during hydration.
// Saved tariffs are matched by `id`; unsaved draft rows are preserved by order.
const createRowIDLookup = (rows: RowItem[] = []): RowIDLookup => {
  const idsByTariffID = new Map<string, string[]>();
  const draftRowIDs: string[] = [];
  const rowsByRowID = new Map<string, RowItem>();

  rows.forEach((row) => {
    rowsByRowID.set(row._rowID, row);
    if (row.id) {
      appendRowID(idsByTariffID, row.id, row._rowID);
      return;
    }

    draftRowIDs.push(row._rowID);
  });

  return { idsByTariffID, draftRowIDs, rowsByRowID };
};

const consumePersistedRowID = (lookup: RowIDLookup, tariffID?: string) =>
  takeNextRowID(tariffID ? lookup.idsByTariffID.get(tariffID) : undefined);

// Reuses the current UI row id whenever possible so row editing state,
// selection, and ordering persist tariff re-hydration.
export const generateInitialRowID = (
  tariff: Partial<RowItem>,
  lookup: RowIDLookup = createRowIDLookup()
) => {
  const existingRowID = tariff._rowID;
  const persistedRowID = consumePersistedRowID(lookup, tariff.id);
  const draftRowID = tariff.id ? '' : takeNextRowID(lookup.draftRowIDs);
  const fallbackRowID = tariff.id
    ? `tariff-${tariff.id}`
    : `row-${Math.random().toString(36).slice(2, 8)}`;

  return existingRowID || persistedRowID || draftRowID || fallbackRowID;
};

export const mapTariffToRowItem = (
  tariff: Partial<RowItem>,
  lookup: RowIDLookup,
  claimServiceStart?: string,
  claimServiceEnd?: string
): RowItem => {
  // Converts a persisted tariff into the table-editing row shape.
  const total =
    tariff.total ?? computeTotalFromRatePct(tariff.rate, tariff.percentage);
  const rowID = generateInitialRowID(tariff, lookup);
  const previousRow = lookup.rowsByRowID.get(rowID);
  const { serviceStart, serviceEnd } = getTariffServiceTimes(
    tariff,
    claimServiceStart,
    claimServiceEnd
  );

  return {
    _rowID: rowID,
    ...tariff,
    units: tariff.units ?? 1,
    icd9: tariff.icd9 || '',
    percentage: tariff.percentage ?? 100,
    split: tariff.split || ' ',
    incision: tariff.incision || null,
    anesthesiaUnits: clampAnesthesiaUnits(Number(tariff.anesthesiaUnits ?? 0)),
    isAnesthesiaUnitsManual: tariff.isAnesthesiaUnitsManual ?? false,
    total,
    isManualTotal: tariff.isManualTotal ?? false,
    serviceStart,
    serviceEnd,
    isDirty: tariff.isDirty ?? false,
    serviceStartOverridden:
      tariff.serviceStartOverridden ??
      previousRow?.serviceStartOverridden ??
      false,
    serviceEndOverridden:
      tariff.serviceEndOverridden ?? previousRow?.serviceEndOverridden ?? false,
    status: tariff.status || DEFAULT_CLAIM_TARIFF_STATUS,
  } as RowItem;
};

export const mapTariffsToRows = (
  tariffs: Partial<RowItem>[] = [],
  claimServiceStart?: string,
  claimServiceEnd?: string,
  previousRows: RowItem[] = []
): RowItem[] => {
  // Hydrates the current tariff list while preserving existing row ids.
  const rowIDLookup = createRowIDLookup(previousRows);

  return tariffs.map((tariff) =>
    mapTariffToRowItem(tariff, rowIDLookup, claimServiceStart, claimServiceEnd)
  );
};

// Keeps recalculated tariffs in the same visible order the user is editing.
export const preserveOrderByRowID = (
  tariffs: RowItem[] = [],
  order: Map<string, number>
) => {
  return [...tariffs].sort((a, b) => {
    const aOrder = order.get(a._rowID) ?? 0;
    const bOrder = order.get(b._rowID) ?? 0;
    return aOrder - bOrder;
  });
};

export const getLatestDebitByTariffID = (
  debits: Debit[] = [],
  predicate?: (debit: Debit) => boolean
) => {
  const byTariffID = new Map<string, Debit>();

  debits.forEach((debit) => {
    if (predicate && !predicate(debit)) return;

    const tariffID = String(debit.tariffID);
    const current = byTariffID.get(tariffID);
    if (
      !current ||
      toDateMillis(debit.createdAt) > toDateMillis(current.createdAt)
    ) {
      byTariffID.set(tariffID, debit);
    }
  });

  return byTariffID;
};

export const getLatestPaidDebitByTariffID = (debits: Debit[] = []) =>
  getLatestDebitByTariffID(
    debits,
    (debit) => Number(debit.paidAmount ?? 0) > 0 && debit.type !== 'RETRACTION'
  );

export const determineAssistPercentage = (
  specialtyCode: string,
  lookup: Array<{ code: string; description: string }>
) => {
  const specialty = lookup.find((s) => s.code === specialtyCode);
  if (!specialty) {
    return 40;
  }
  const isGP = specialty.description.split(' ')[0] === 'General';
  return isGP ? 40 : 60;
};

const generateRowID = () =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const generateNewRowItem = ({
  last,
  claimServiceStart,
  claimServiceEnd,
  tariffs = [],
}: {
  last: RowItem | null;
  claimServiceStart?: string;
  claimServiceEnd?: string;
  tariffs?: Array<Pick<RowItem, 'serviceStart' | 'serviceEnd' | 'isDirty'>>;
}): RowItem => {
  // New rows inherit the most recent dirty timing to avoid resetting to claim start
  const latestDirtyTariff = [...tariffs]
    .reverse()
    .find((tariff) => tariff.isDirty);
  const inheritedDirtyStart = normalizeTariffDateTime(
    latestDirtyTariff?.serviceStart
  );
  const inheritedDirtyEnd = normalizeTariffDateTime(
    latestDirtyTariff?.serviceEnd
  );

  const serviceStart =
    inheritedDirtyStart ||
    getNextTariffDateTime(tariffs, claimServiceStart, 'serviceStart') ||
    normalizeTariffDateTime(claimServiceStart);
  const serviceEnd =
    inheritedDirtyEnd ||
    normalizeTariffDateTime(claimServiceEnd) ||
    getDefaultTariffServiceEnd(serviceStart);
  const isDirty = Boolean(latestDirtyTariff && serviceStart);

  return {
    _rowID: generateRowID(),
    id: v4(),
    units: 1,
    icd9: last?.icd9 || '',
    code: '',
    prefix: 0,
    description: null,
    split: ' ',
    incision: null,
    anesthesiaUnits: 0,
    isAnesthesiaUnitsManual: false,
    effectiveDate: null,
    specialtyCode: null,
    type: 'Base',
    rate: '0.00',
    originalRate: '0.00',
    percentage: 100,
    total: 0,
    serviceStart,
    serviceEnd,
    isDirty,
    serviceStartOverridden: false,
    serviceEndOverridden: false,
    isManualTotal: false,
    status: DEFAULT_CLAIM_TARIFF_STATUS,
    province: last?.province || '',
  };
};

export const createBlankTariff = (
  last?: RowItem | null,
  claimServiceStart?: string,
  claimServiceEnd?: string
): RowItem =>
  generateNewRowItem({
    last: last || null,
    claimServiceStart,
    claimServiceEnd,
  });
